// Approvals — the human-in-the-loop queue. Every autonomous action that
// crosses an approval rule waits here for a person to allow or decline it.
import { useState } from "react";
import posthog from "@/lib/posthog";
import { Icon } from "@/components/Icon";
import { SectionHead } from "@/components/primitives";
import { useNotImplemented } from "@/hooks/use-not-implemented";
import { DATA } from "@/data";

type Approval = (typeof DATA.approvals)[number];
type Decision = "approved" | "rejected";

const RISK_BADGE: Record<string, [string, string]> = {
  low: ["b-pos", "Low risk"], med: ["b-warn", "Medium risk"], high: ["b-neg", "High risk"],
};

const AgentMark = ({ a }: { a: Approval }) => (
  <span style={{ width: 38, height: 38, borderRadius: 10, background: a.color + "18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
    <Icon name={a.icon} size={19} stroke={a.color} />
  </span>
);

const PendingCard = ({ a, decision, onDecide }: { a: Approval; decision?: Decision; onDecide: (id: string, d: Decision) => void }) => {
  const notify = useNotImplemented();
  return (
  <div className="card" style={{ padding: "18px 20px", opacity: decision ? 0.62 : 1, transition: "opacity .2s" }}>
    <div style={{ display: "flex", gap: 13, alignItems: "flex-start" }}>
      <AgentMark a={a} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 3 }}>
          <span style={{ fontSize: 14.5, fontWeight: 600 }}>{a.title}</span>
          <span className={"badge " + RISK_BADGE[a.risk][0]}>{RISK_BADGE[a.risk][1]}</span>
          <span className="badge b-mute">{a.kind}</span>
          <span style={{ marginLeft: "auto", fontSize: 11.5, color: "var(--ink-4)", flexShrink: 0 }}>{a.time}</span>
        </div>
        <div style={{ fontSize: 12, color: "var(--ink-4)", marginBottom: 8 }}>
          Proposed by <b style={{ color: "var(--ink-2)" }}>{a.agent}</b> · {a.conf}% confidence
        </div>
        <div style={{ fontSize: 13, color: "var(--ink-3)", lineHeight: 1.55, maxWidth: 720 }}>{a.desc}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 14 }}>
          {decision ? (
            <span className={"badge " + (decision === "approved" ? "b-pos" : "b-neg")} style={{ height: 26 }}>
              <Icon name={decision === "approved" ? "check" : "x"} size={13} stroke={decision === "approved" ? "var(--pos)" : "var(--neg)"} />
              {decision === "approved" ? "Approved — agent is executing" : "Declined — agent notified"}
            </span>
          ) : (
            <>
              <button className="btn btn-pri btn-sm" onClick={() => onDecide(a.id, "approved")}>
                <Icon name="check" size={14} stroke="#fff" /> Approve
              </button>
              <button className="btn btn-sec btn-sm" onClick={() => onDecide(a.id, "rejected")}>Decline</button>
              <span className="link" style={{ fontSize: 12.5, cursor: "pointer" }} onClick={() => { posthog.capture("approval_ask_ai_clicked", { id: a.id, title: a.title, agent: a.agent }); notify("Ask AI why"); }}>Ask AI why</span>
            </>
          )}
          <span className="num" style={{ marginLeft: "auto", fontSize: 13.5, fontWeight: 700 }}>{a.amount}</span>
          <span className="badge b-brand">{a.impact}</span>
        </div>
      </div>
    </div>
  </div>
  );
};

export const ApprovalsScreen = () => {
  const notify = useNotImplemented();
  const [decided, setDecided] = useState<Record<string, Decision>>({});
  const pending = DATA.approvals.filter((a) => a.status === "pending");
  const resolved = DATA.approvals.filter((a) => a.status !== "pending");
  const open = pending.filter((a) => !decided[a.id]).length;
  const decide = (id: string, d: Decision) => {
    const a = DATA.approvals.find((x) => x.id === id);
    posthog.capture("approval_decided", { id, decision: d, title: a?.title, agent: a?.agent, amount: a?.amount, risk: a?.risk });
    setDecided((m) => ({ ...m, [id]: d }));
  };
  return (
    <div style={{ maxWidth: 880, margin: "0 auto" }} className="fade-up">
      <SectionHead title="Approvals" size="lg"
        sub="Agent actions that crossed an approval rule wait here. Everything else runs autonomously and shows up in the activity timeline."
        actions={<button className="btn btn-sec" onClick={() => { posthog.capture("approval_rules_clicked"); notify("Approval rules"); }}><Icon name="sliders" size={15} stroke="var(--ink-3)" /> Approval rules</button>} />

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
        <span className="eyebrow">Waiting for you</span>
        <span className="chip" style={{ height: 22 }}>{open} pending</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 28 }}>
        {pending.map((a) => <PendingCard key={a.id} a={a} decision={decided[a.id]} onDecide={decide} />)}
      </div>

      <div className="eyebrow" style={{ marginBottom: 12 }}>Recently resolved</div>
      <div className="card">
        {resolved.map((a, i) => (
          <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "13px 18px", borderBottom: i < resolved.length - 1 ? "1px solid var(--line-2)" : "none" }}>
            <AgentMark a={a} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{a.title}</div>
              <div style={{ fontSize: 11.5, color: "var(--ink-4)", marginTop: 1 }}>{a.desc}</div>
            </div>
            <div style={{ textAlign: "right", flexShrink: 0 }}>
              <span className={"badge " + (a.status === "approved" ? "b-pos" : "b-neg")}>{a.status === "approved" ? "Approved" : "Declined"}</span>
              <div style={{ fontSize: 11, color: "var(--ink-4)", marginTop: 3 }}>{(a as { by?: string }).by} · {a.time}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
